import { FC, useCallback, useEffect, useState } from 'react';
import { User } from '@core/generated/gql-types';
import { useJobStore } from '@core/job/useJobStore';
import JobsTable from '../job/JobsTable';
import { useJobsTable } from '../job/useJobsTable';
import { useNavigate } from 'react-router-dom';
import { SkeletonTable } from '../shared/Skeleton';

interface JobsProps {
  worker: User;
}

const JOBS_PER_PAGE = 100;

const Jobs: FC<JobsProps> = ({ worker }) => {
  const { getJobsByWorkerId, isLoading, fetchAllJobsByStatusForUserId } =
    useJobStore();
  const jobs = getJobsByWorkerId(worker.id);
  const navigate = useNavigate();
  const {
    sortField,
    sortDirection,
    handleSort,
    paginatedFilteredJobs,
    pagination,
  } = useJobsTable(jobs);

  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | null>(null);
  const [hasMoreFromServer, setHasMoreFromServer] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const handleJobClick = (jobId: string) => {
    navigate(`/jobs/${jobId}`);
  };

  useEffect(() => {
    if (!worker.id) {
      return;
    }

    let isCancelled = false;

    const fetchFirstPage = async () => {
      setLastEvaluatedKey(null);
      setHasMoreFromServer(false);

      const response = await fetchAllJobsByStatusForUserId(
        [],
        worker.id,
        JOBS_PER_PAGE,
      );
      if (isCancelled) {
        return;
      }

      setLastEvaluatedKey(response.lastEvaluatedKey ?? null);
      setHasMoreFromServer(Boolean(response.hasMore));
    };

    void fetchFirstPage();

    return () => {
      isCancelled = true;
    };
  }, [fetchAllJobsByStatusForUserId, worker.id]);

  const fetchNextPage = useCallback(async () => {
    if (
      !worker.id ||
      !hasMoreFromServer ||
      isFetchingMore ||
      !lastEvaluatedKey
    ) {
      return;
    }

    setIsFetchingMore(true);
    try {
      const response = await fetchAllJobsByStatusForUserId(
        [],
        worker.id,
        JOBS_PER_PAGE,
        lastEvaluatedKey,
        { suppressGlobalLoading: true },
      );
      setLastEvaluatedKey(response.lastEvaluatedKey ?? null);
      setHasMoreFromServer(Boolean(response.hasMore));
    } finally {
      setIsFetchingMore(false);
    }
  }, [
    worker.id,
    hasMoreFromServer,
    isFetchingMore,
    lastEvaluatedKey,
    fetchAllJobsByStatusForUserId,
  ]);

  const handlePageChange = useCallback(
    (page: number) => {
      pagination.onPageChange(page);

      const isGoingToLastPage = page >= pagination.totalPages;
      if (isGoingToLastPage && hasMoreFromServer && !isFetchingMore) {
        void fetchNextPage();
      }
    },
    [pagination, hasMoreFromServer, isFetchingMore, fetchNextPage],
  );

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-xl font-semibold">Jobs</h2>
      <div className="space-y-4">
        <p className="text-gray-600">
          View jobs assigned to {worker.firstName} {worker.lastName}
        </p>
        {isLoading && jobs.length === 0 ? (
          <SkeletonTable />
        ) : (
          <JobsTable
            jobs={paginatedFilteredJobs}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            onJobClick={handleJobClick}
            showClientColumn={false}
            pagination={{
              ...pagination,
              onPageChange: handlePageChange,
              hasMore: hasMoreFromServer || pagination.hasMore,
            }}
            isLoading={isLoading || isFetchingMore}
          />
        )}
      </div>
    </div>
  );
};

export default Jobs;
